<template>
    <div class="favourites-library overflow-y-auto h-screen-header" ref="favourateview">
        <section class="favourites-hero">
            <div class="favourites-hero__copy">
                <div class="favourites-hero__label">{{ $tt('我的喜爱') }}</div>
                <h1>{{ pageTitle }}</h1>
                <p>{{ summaryText }}</p>
                <div class="favourites-hero__actions">
                    <button
                        v-if="sortValue === 'tracks'"
                        type="button"
                        class="btn btn-primary btn-sm"
                        :disabled="!activeItems.length"
                        @click="playTracks"
                    >
                        {{ $tt('播放全部') }}
                    </button>
                    <button
                        v-if="sortValue === 'tracks'"
                        type="button"
                        class="btn btn-outline btn-sm"
                        :disabled="!activeItems.length"
                        @click="shuffleTracks"
                    >
                        {{ $tt('随机播放') }}
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" :disabled="loading" @click="refreshCurrentPage">
                        {{ $tt('刷新') }}
                    </button>
                </div>
            </div>

            <div class="favourites-hero__stage" aria-hidden="true">
                <div class="favourites-stack">
                    <div
                        v-for="(item, index) in heroItems"
                        :key="`${item.id || index}_${index}`"
                        class="favourites-stack__item"
                        :style="{ '--favourite-index': index }"
                    >
                        <img :src="item.image || '/static/dist/img/album_null.jpg'" alt="">
                        <div>
                            <strong>{{ itemTitle(item) }}</strong>
                            <span>{{ itemSubtitle(item) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="favourites-toolbar">
            <div class="favourites-toolbar__tabs scrollbar-hide">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    type="button"
                    :class="['favourites-tab', sortValue === value ? 'is-active' : '']"
                    @click="switchSort(value)"
                >
                    {{ text }}
                </button>
            </div>
        </section>

        <section class="favourites-status-strip">
            <div>
                <span class="favourites-status-strip__label">{{ $tt('当前分类') }}</span>
                <strong>{{ activeLabel }}</strong>
            </div>
            <div>
                <span class="favourites-status-strip__label">{{ $tt('已载入') }}</span>
                <strong>{{ activeItems.length }}</strong>
            </div>
            <div>
                <span class="favourites-status-strip__label">{{ $tt('专辑') }}</span>
                <strong>{{ details.albums.length }}</strong>
            </div>
            <div>
                <span class="favourites-status-strip__label">{{ $tt('艺术家') }}</span>
                <strong>{{ details.artists.length }}</strong>
            </div>
            <div>
                <span class="favourites-status-strip__label">{{ $tt('歌曲') }}</span>
                <strong>{{ details.tracks.length }}</strong>
            </div>
        </section>

        <section class="favourites-content">
            <ArtistList v-if="sortValue === 'artists' && activeItems.length" :items="details.artists" />
            <TrackList v-else-if="sortValue === 'tracks' && activeItems.length" :tracks="details.tracks" :index-field="'index'" />
            <AlbumList v-else-if="activeItems.length" :items="details.albums" />

            <div v-if="loading" class="favourites-loading">
                <div class="favourites-loading__grid">
                    <span v-for="index in 8" :key="index"></span>
                </div>
                <p>{{ $tt('正在载入收藏') }}</p>
            </div>

            <div v-if="!loading && activeItems.length === 0" class="favourites-empty">
                <div class="favourites-empty__art">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <h3>{{ $tt('这里还没有内容') }}</h3>
                <p>{{ emptyText }}</p>
            </div>
        </section>

        <button v-if="curScrollTop > 700" @click="scrollToTop" class="favourites-scroll-top" :title="$tt('返回顶部')">
            <svg class="icon h-5 w-5 fill-current" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3c-3.8 5.3-0.1 12.7 6.5 12.7h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path>
            </svg>
        </button>
    </div>
</template>

<script>
import AlbumList from '@/views/library/album/AlbumList.vue'
import ArtistList from '@/views/library/artist/ArtistList.vue'
import TrackList from '@/views/library/track/TrackList.vue'

export default {
    name: 'favourites',
    components: {
        AlbumList,
        ArtistList,
        TrackList
    },
    data() {
        return {
            options: [
                { text: this.$tt('专辑'), value: 'albums' },
                { text: this.$tt('艺术家'), value: 'artists' },
                { text: this.$tt('歌曲'), value: 'tracks' }
            ],
            loading: false,
            details: { albums: [], artists: [], tracks: [] },
            curScrollTop: 0,
            offset: 0,
            hasMore: true,
            isScrollBound: false
        }
    },
    computed: {
        sortValue() {
            return this.$route.params.sort || 'albums'
        },
        activeItems() {
            return this.details[this.sortValue] || []
        },
        activeLabel() {
            const labelMap = {
                albums: this.$tt('专辑'),
                artists: this.$tt('艺术家'),
                tracks: this.$tt('歌曲')
            }
            return labelMap[this.sortValue] || this.$tt('内容')
        },
        pageTitle() {
            const titleMap = {
                albums: this.$tt('喜爱的专辑'),
                artists: this.$tt('喜爱的艺术家'),
                tracks: this.$tt('喜爱的歌曲')
            }
            return titleMap[this.sortValue] || this.$tt('我的喜爱')
        },
        summaryText() {
            const textMap = {
                albums: this.$tt('把常听专辑集中放在这里，随时继续播放和整理。'),
                artists: this.$tt('收藏过的艺术家会整理在这里，方便继续探索。'),
                tracks: this.$tt('你标记过的歌曲会留在这里，适合直接开播。')
            }
            return textMap[this.sortValue] || ''
        },
        emptyText() {
            const textMap = {
                albums: this.$tt('还没有喜爱的专辑，可以先去发现页收下几张。'),
                artists: this.$tt('还没有喜爱的艺术家，先去挑几个喜欢的声音。'),
                tracks: this.$tt('还没有喜爱的歌曲，点亮心形后会出现在这里。')
            }
            return textMap[this.sortValue] || ''
        },
        heroItems() {
            const items = this.activeItems.slice(0, 4)
            while (items.length < 4) {
                items.push({})
            }
            return items
        }
    },
    watch: {
        sortValue(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.offset = this.activeItems.length
                this.hasMore = true
                this.restoreScroll(0)
                if (this.activeItems.length === 0) {
                    this.loadMore()
                    return
                }
                this.$nextTick(() => {
                    this.listenBottomOut()
                })
            }
        }
    },
    created() {
        this.loadMore()
    },
    mounted() {
        this.bindScrollListener()
    },
    activated() {
        this.bindScrollListener()
        this.restoreScroll()
    },
    deactivated() {
        this.unbindScrollListener()
    },
    beforeDestroy() {
        this.unbindScrollListener()
    },
    methods: {
        bindScrollListener() {
            if (this.$refs.favourateview && !this.isScrollBound) {
                this.$refs.favourateview.addEventListener('scroll', this.listenBottomOut)
                window.favourateview = this.$refs.favourateview
                this.isScrollBound = true
            }
        },
        unbindScrollListener() {
            if (this.$refs.favourateview && this.isScrollBound) {
                this.$refs.favourateview.removeEventListener('scroll', this.listenBottomOut, false)
                this.isScrollBound = false
            }
        },
        restoreScroll(value = this.curScrollTop) {
            if (this.$refs.favourateview) {
                this.$refs.favourateview.scrollTop = value || 0
            }
        },
        switchSort(value) {
            if (this.sortValue === value) {
                this.refreshCurrentPage()
                return
            }
            this.$router.push({ path: '/favourites/' + value })
        },
        refreshCurrentPage() {
            this.details[this.sortValue] = []
            this.offset = 0
            this.hasMore = true
            return this.loadMore()
        },
        loadMore() {
            if (this.loading || !this.hasMore) {
                return null
            }
            this.loading = true
            return this.$api.subsonic.getFavourites(this.sortValue, 30, this.offset).then(details => {
                const items = details[this.sortValue] || []
                this.details[this.sortValue].push(...items)
                this.offset += items.length
                this.hasMore = items.length > 0
                this.loading = false
            }).catch((err) => {
                this.loading = false
                if (this.$myMsg && err && err.message !== 'Wrong username or password.') {
                    this.$myMsg.notify({
                        title: this.$tt('载入失败'),
                        content: err.message || this.$tt('收藏载入失败'),
                        type: 'error',
                        time: 5000,
                    })
                }
            })
        },
        listenBottomOut() {
            const container = this.$refs.favourateview || window.favourateview
            if (!container) {
                return
            }
            const scrollTop = container.scrollTop || document.body.scrollTop
            const scrollHeight = container.scrollHeight || document.body.scrollHeight
            const clientHeight = container.clientHeight || document.body.clientHeight
            this.curScrollTop = scrollTop
            if (this.hasMore && !this.loading && scrollTop + clientHeight >= scrollHeight - 80) {
                this.loadMore()
            }
        },
        scrollToTop() {
            if (this.$refs.favourateview) {
                this.$refs.favourateview.scrollTop = 0
            }
        },
        playTracks() {
            if (!this.details.tracks.length) {
                return
            }
            this.$store.dispatch('playTrackList', {
                tracks: this.details.tracks
            })
        },
        shuffleTracks() {
            if (!this.details.tracks.length) {
                return
            }
            const index = Math.floor(Math.random() * this.details.tracks.length)
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.details.tracks
            })
        },
        itemTitle(item) {
            if (this.sortValue === 'tracks') {
                return item.title || this.$tt('未命名歌曲')
            }
            return item.name || this.$tt('未命名内容')
        },
        itemSubtitle(item) {
            if (this.sortValue === 'artists') {
                return this.$tt('{count} 张专辑', { count: Number(item.albumCount || 0) })
            }
            if (this.sortValue === 'tracks') {
                return item.artist || this.$tt('未知艺术家')
            }
            return item.artist || this.$tt('未知艺术家')
        }
    }
}
</script>

<style scoped>
.favourites-library {
    --favourites-radius: 18px;
    --favourites-border: oklch(var(--bc) / 0.12);
    --favourites-muted: oklch(var(--bc) / 0.62);
    --favourites-soft: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    min-height: 0;
    overflow-x: hidden;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
    padding: 18px;
}

.favourites-hero,
.favourites-toolbar,
.favourites-status-strip,
.favourites-content {
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
}

.favourites-hero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(300px, 0.85fr);
    gap: 28px;
    align-items: stretch;
    min-height: 226px;
    margin-bottom: 14px;
    border: 1px solid var(--favourites-border);
    border-radius: var(--favourites-radius);
    background:
        radial-gradient(circle at 86% 18%, color-mix(in oklch, oklch(var(--p)) 18%, transparent), transparent 24rem),
        linear-gradient(
            105deg,
            oklch(var(--b1) / 0.98) 0%,
            oklch(var(--b1) / 0.96) 42%,
            color-mix(in oklch, oklch(var(--b2)) 88%, oklch(var(--p)) 6%) 68%,
            color-mix(in oklch, oklch(var(--b3)) 84%, oklch(var(--p)) 10%) 100%
        ),
        oklch(var(--b1));
    box-shadow: 0 20px 60px oklch(var(--bc) / 0.08);
    isolation: isolate;
    overflow: hidden;
}

.favourites-hero__copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: clamp(24px, 4vw, 44px);
}

.favourites-hero__label {
    width: fit-content;
    margin-bottom: 14px;
    padding: 5px 10px;
    border-radius: 999px;
    background: oklch(var(--p) / 0.14);
    color: oklch(var(--p));
    font-size: 12px;
    font-weight: 700;
}

.favourites-hero h1 {
    max-width: 700px;
    margin: 0;
    font-size: clamp(30px, 4vw, 54px);
    line-height: 1.04;
    font-weight: 800;
    letter-spacing: 0;
}

.favourites-hero p {
    max-width: 55ch;
    margin-top: 16px;
    color: var(--favourites-muted);
    font-size: 15px;
    line-height: 1.75;
}

.favourites-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
}

.favourites-hero__stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 226px;
    padding: 24px;
    background: transparent;
    isolation: isolate;
    overflow: hidden;
}

.favourites-hero__stage::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        linear-gradient(
            90deg,
            transparent 0%,
            color-mix(in oklch, oklch(var(--b3)) 42%, transparent) 46%,
            color-mix(in oklch, oklch(var(--b3)) 68%, oklch(var(--p)) 8%) 100%
        );
    pointer-events: none;
}

.favourites-stack {
    position: relative;
    z-index: 1;
    width: min(450px, 100%);
    min-height: 216px;
}

.favourites-stack__item {
    position: absolute;
    left: calc(var(--favourite-index) * 7%);
    top: calc(var(--favourite-index) * 16px);
    display: grid;
    width: min(340px, 82vw);
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid oklch(var(--bc) / 0.13);
    border-radius: 16px;
    background:
        linear-gradient(135deg, oklch(var(--b1) / 0.9), oklch(var(--b2) / 0.88)),
        oklch(var(--b1));
    box-shadow: 0 14px 34px oklch(var(--bc) / 0.14);
}

.favourites-stack__item img {
    width: 64px;
    aspect-ratio: 1;
    border-radius: 12px;
    object-fit: cover;
}

.favourites-stack__item strong,
.favourites-stack__item span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.favourites-stack__item strong {
    font-size: 14px;
    font-weight: 800;
}

.favourites-stack__item span {
    margin-top: 5px;
    color: var(--favourites-muted);
    font-size: 12px;
}

.favourites-toolbar {
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding: 10px;
    border: 1px solid var(--favourites-border);
    border-radius: var(--favourites-radius);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(14px);
    z-index: 3;
}

.favourites-toolbar__tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 1px;
}

.favourites-tab {
    flex: 0 0 auto;
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: transparent;
    color: oklch(var(--bc) / 0.72);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.favourites-tab:hover {
    background: oklch(var(--b2));
    color: oklch(var(--bc));
}

.favourites-tab:active,
.favourites-scroll-top:active {
    transform: translateY(1px);
}

.favourites-tab.is-active {
    background: oklch(var(--p));
    color: oklch(var(--pc));
    border-color: oklch(var(--p));
}

.favourites-status-strip {
    display: grid;
    grid-template-columns: 1.25fr 1fr 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
}

.favourites-status-strip > div {
    min-width: 0;
    padding: 14px 16px;
    border: 1px solid var(--favourites-border);
    border-radius: 16px;
    background: var(--favourites-soft);
}

.favourites-status-strip__label {
    display: block;
    margin-bottom: 3px;
    color: var(--favourites-muted);
    font-size: 12px;
}

.favourites-status-strip strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 18px;
}

.favourites-content {
    padding: 2px 0 28px;
}

.favourites-loading,
.favourites-empty {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border: 1px solid var(--favourites-border);
    border-radius: var(--favourites-radius);
    background: oklch(var(--b1) / 0.74);
    text-align: center;
}

.favourites-loading__grid {
    display: grid;
    width: min(500px, 78vw);
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.favourites-loading__grid span {
    aspect-ratio: 1.25;
    border-radius: 16px;
    background: linear-gradient(110deg, oklch(var(--b2)), oklch(var(--b3)), oklch(var(--b2)));
    background-size: 200% 100%;
}

.favourites-loading p,
.favourites-empty p {
    max-width: 38ch;
    color: var(--favourites-muted);
    font-size: 14px;
}

.favourites-empty__art {
    display: grid;
    width: 132px;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.favourites-empty__art span {
    aspect-ratio: 0.78;
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 14px;
    background: oklch(var(--b2));
}

.favourites-empty__art span:nth-child(2) {
    transform: translateY(-14px);
    background: oklch(var(--p) / 0.22);
}

.favourites-empty h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
}

.favourites-scroll-top {
    position: fixed;
    right: 24px;
    bottom: 24px;
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: oklch(var(--p));
    color: oklch(var(--pc));
    box-shadow: 0 12px 30px oklch(var(--bc) / 0.2);
    transition: transform 180ms ease, background-color 180ms ease;
    z-index: 4;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

@media (prefers-reduced-motion: no-preference) {
    .favourites-hero,
    .favourites-toolbar,
    .favourites-status-strip,
    .favourites-content {
        animation: favouritesFadeIn 360ms ease both;
    }

    .favourites-loading__grid span {
        animation: favouritesSkeleton 1.2s ease-in-out infinite;
    }
}

@keyframes favouritesFadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes favouritesSkeleton {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

@media screen and (max-width: 900px) {
    .favourites-library {
        padding: 12px;
    }

    .favourites-hero {
        grid-template-columns: 1fr;
    }

    .favourites-hero__stage {
        min-height: 210px;
    }

    .favourites-status-strip {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media screen and (max-width: 640px) {
    .favourites-hero__copy {
        padding: 22px;
    }

    .favourites-hero h1 {
        font-size: 30px;
    }

    .favourites-stack {
        width: 100%;
        min-height: 214px;
    }

    .favourites-stack__item {
        left: calc(var(--favourite-index) * 4%);
        width: 88%;
    }

    .favourites-status-strip {
        grid-template-columns: 1fr;
    }

    .favourites-loading__grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .favourites-scroll-top {
        right: 16px;
        bottom: 16px;
    }
}
</style>
